import { getErrorTranslations } from "lib/i18n"
import { joinName, tryCatchFallback } from "lopermut"
import { useUpdateCustomerMutation } from "modules/customers/hooks/useUpdateCustomerMutation"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerResponseDataSchema, Errors, UpsertCustomerBodySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type CustomerFormValues = {
  firstName: string | null | undefined
  lastName: string | null | undefined
  tentativeFirstName: string | null | undefined
  tentativeLastName: string | null | undefined
  email: string | null | undefined
  tentativeEmail: string | null | undefined
  phone: string | null | undefined
  tentativePhone: string | null | undefined
  metadata: Record<string, unknown> | null | string | number | boolean
  country: string | null | undefined
  locale: string | null | undefined
  useChannel: boolean
  clientId: string | null
  tags: string[]
}

type UseCustomerFormProps = {
  customer: z.infer<typeof CustomerResponseDataSchema>
  conversationId?: string
}

function getInitialValues(customer: z.infer<typeof CustomerResponseDataSchema>) {
  return {
    firstName: customer.firstName ?? (customer.lastName ? null : customer.name),
    lastName: customer.lastName,
    tentativeFirstName:
      customer.tentativeFirstName ?? (customer.tentativeLastName ? null : customer.tentativeName),
    tentativeLastName: customer.tentativeLastName,
    email: customer.email,
    tentativeEmail: customer.tentativeEmail,
    phone: customer.phone,
    tentativePhone: customer.tentativePhone,
    metadata: customer.metadata,
    country: customer.country,
    locale: customer.locale,
    useChannel: customer.useChannel,
    clientId: customer.client?.id ?? null,
    tags: getPermutTagNames(customer),
  }
}

// Compares what would be persisted: "" equals null, tag order and metadata formatting don't count.
function serialize(values: CustomerFormValues) {
  const metadata =
    typeof values.metadata === "string"
      ? tryCatchFallback(() => JSON.parse(values.metadata as string), values.metadata)
      : values.metadata

  return JSON.stringify({
    firstName: values.firstName || null,
    lastName: values.lastName || null,
    email: values.email || null,
    phone: values.phone || null,
    country: values.country || null,
    locale: values.locale || null,
    clientId: values.clientId || null,
    useChannel: values.useChannel,
    tags: [...values.tags].sort(),
    metadata: metadata === "" ? null : metadata,
  })
}

function isSameValues(a: CustomerFormValues, b: CustomerFormValues) {
  return serialize(a) === serialize(b)
}

export function useCustomerForm({ customer, conversationId }: UseCustomerFormProps) {
  const { t } = useTranslation()

  const [formValues, setFormValues] = useState<CustomerFormValues>(() => getInitialValues(customer))
  const formValuesRef = useRef(formValues)
  const savedValuesRef = useRef(formValues)

  useEffect(() => {
    // Don't clobber in-progress local edits with the server snapshot.
    if (!isSameValues(formValuesRef.current, savedValuesRef.current)) return
    const next = getInitialValues(customer)
    formValuesRef.current = next
    savedValuesRef.current = next
    setFormValues(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(customer)])

  const { mutate: updateCustomer } = useUpdateCustomerMutation({
    customerId: customer.id,
    conversationId,
  })

  function updateFormValue<K extends keyof CustomerFormValues>(
    field: K,
    value: CustomerFormValues[K],
  ) {
    formValuesRef.current = { ...formValuesRef.current, [field]: value }
    setFormValues(formValuesRef.current)
  }

  function save() {
    const values = formValuesRef.current
    const previousValues = savedValuesRef.current
    if (isSameValues(values, previousValues)) return

    const metadata =
      typeof values.metadata === "object"
        ? tryCatchFallback(() => JSON.stringify(values.metadata, null, 2), {})
        : values.metadata

    const hasNameChanged =
      (values.firstName || null) !== (previousValues.firstName || null) ||
      (values.lastName || null) !== (previousValues.lastName || null)

    const data = {
      tags: {
        add: values.tags.filter((name) => !previousValues.tags.includes(name)),
        remove: previousValues.tags.filter((name) => !values.tags.includes(name)),
      },
      ...(hasNameChanged && {
        name: joinName(values.firstName, values.lastName),
        firstName: values.firstName || null,
        lastName: values.lastName || null,
      }),
      email: values.email,
      phone: values.phone,
      metadata,
      country: values.country,
      locale: values.locale,
      useChannel: values.useChannel,
      clientId: values.clientId,
    }

    const schema = UpsertCustomerBodySchema.safeParse(data)

    if (schema.error) {
      const errorTranslations = getErrorTranslations()
      const errorText = schema.error.issues[0]?.message as Errors | undefined

      toast.error(errorTranslations[errorText ?? Errors.GENERIC_ERROR])
      return
    }

    savedValuesRef.current = values
    // Failure toast and Sentry reporting come from the global mutation onError in main.tsx
    updateCustomer(data, {
      onSuccess: () => toast.success(t("customers.editSuccess")),
      onError: () => {
        savedValuesRef.current = previousValues
      },
    })
  }

  function updateAndSave<K extends keyof CustomerFormValues>(
    field: K,
    value: CustomerFormValues[K],
  ) {
    updateFormValue(field, value)
    save()
  }

  return { formValues, updateFormValue, updateAndSave, save }
}

function getPermutTagNames(customer: z.infer<typeof CustomerResponseDataSchema>) {
  return (customer.tags ?? []).filter((tag) => tag.source === "Permut").map((tag) => tag.name)
}
